import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { clearAllDraftsForUser } from './utils/tacticsDrafts';
import {
  markJourneyStepComplete,
  completeJourneyStep,
  mergeServerProgress,
  flushPendingJourneySteps,
  readProgress,
} from './utils/journeyProgress';
import { getJourneyConfig } from './config/onboardingCatalog';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import MenuIcon from '@mui/icons-material/Menu';
import './App.css';
import { authAPI, notificationsAPI, parentAPI, parentMessagesAPI } from './services/api';
import api from './services/api';
import getStoredUser from './utils/getStoredUser';
import RegisterPage from './components/RegisterPage';
import VerifyEmail from './components/VerifyEmail';
import ResetPassword from './components/ResetPassword';
import SettingsPanel from './components/SettingsPanel';
import ErrorBoundary from './components/ErrorBoundary';
import RouteSuspense from './components/RouteSuspense';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import GuidedTour from './components/GuidedTour';
import PendingApproval from './components/PendingApproval';
import NoClubGate from './components/NoClubGate';
import CreateClubWizard from './components/CreateClubWizard';
import ClubOnboarding from './components/ClubOnboarding';
import CoachEntryGate from './components/CoachEntryGate';
import { CoachExperienceProvider } from './context/CoachExperienceContext';
import ClubLogo from './components/ClubLogo';
import InviteAcceptPage from './components/InviteAcceptPage';
import MobileInstallPrompt from './components/MobileInstallPrompt';
import { TeamProvider, useTeam } from './context/TeamContext';
import { StatConfigProvider } from './context/StatConfigContext';
import { LocaleProvider, detectDeviceLocale, termsForLocale } from './context/LocaleContext';
import UserJourney from './components/UserJourney';
import ParentInviteAccept from './components/ParentInviteAccept';
import OAuthConsentPage from './components/OAuthConsentPage';
import LegalDocumentPage from './components/LegalDocumentPage';
import BrandLogo from './components/BrandLogo';
import NotificationSettings from './components/NotificationSettings';
import NotificationDiscoveryPrompt from './components/NotificationDiscoveryPrompt';
import AdminStepUpDialog from './components/AdminStepUpDialog';
import {
  clearLoginGeneration,
  createLoginGeneration,
  readLoginGeneration,
} from './utils/notificationDiscovery';

// R217.0A — lazy-loaded route content (data-driven from bundle baseline).
// Eager-only kept for: login flow, gates/intermediates, in-layout overlays
// (SettingsPanel, GuidedTour, UserJourney) that mount alongside <Routes>.
const AICoachChat = lazy(() => import('./components/AICoachChat'));
const TrainingPlanGenerator = lazy(() => import('./components/TrainingPlanGenerator'));
const TrainingCalendar = lazy(() => import('./components/TrainingCalendar'));
const TodaySession = lazy(() => import('./components/TodaySession'));
const TrainingBlocks = lazy(() => import('./components/TrainingBlocks'));
const DashboardPage = lazy(() => import('./components/Dashboard'));
const TacticsBoard = lazy(() => import('./components/TacticsBoard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const FilmRoom = lazy(() => import('./components/FilmRoom'));
const ScoutLibrary = lazy(() => import('./components/ScoutLibrary'));
const PlayerRoster = lazy(() => import('./components/PlayerRoster'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const CompanyPage = lazy(() => import('./components/CompanyPage'));
const TeamDocuments = lazy(() => import('./components/TeamDocuments'));
const HelpPage = lazy(() => import('./components/HelpPage'));
const ClubChat = lazy(() => import('./components/ClubChat'));
const TeamManager = lazy(() => import('./components/TeamManager'));
const PlayerFolder = lazy(() => import('./components/PlayerFolder'));
const TeamProfile = lazy(() => import('./components/TeamProfile'));
const ParentDashboard = lazy(() => import('./components/ParentDashboard'));
const ParentMessages = lazy(() => import('./components/ParentMessages'));
const FeedbackModal = lazy(() => import('./components/FeedbackModal'));

const drawerWidth = 240;
const FEEDBACK_BUTTON_EDGE = 48;

const clampFeedbackPosition = (position, bottomInset = 0) => {
  if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y) || typeof window === 'undefined') {
    return null;
  }
  const maxX = Math.max(0, window.innerWidth - FEEDBACK_BUTTON_EDGE);
  const maxY = Math.max(0, window.innerHeight - FEEDBACK_BUTTON_EDGE - bottomInset);
  return {
    x: Math.max(0, Math.min(maxX, position.x)),
    y: Math.max(0, Math.min(maxY, position.y)),
  };
};

const LEGACY_SELF_SERVE_ONBOARDING = process.env.REACT_APP_SELF_SERVE_ONBOARDING === 'on';
const CLUB_ONBOARDING_MODE = process.env.REACT_APP_CLUB_ONBOARDING_MODE
  || (LEGACY_SELF_SERVE_ONBOARDING ? 'join_or_create' : 'off');
const CLUB_ONBOARDING_ENABLED = CLUB_ONBOARDING_MODE !== 'off';

const FONT_SIZE_MAP = { S: 14, M: 15, L: 17 };

// R217.1 Sub-PR 1: mobile bottom-nav tab definitions. 5 fixed tabs across all
// plan tiers (UX best practice: 3-5 visible, consistent across users). Pro-
// gated destinations like /today-session and /calendar still render their own
// upsell when tapped by free users — no plan check at the nav level.
// `kind: 'drawer'` opens the side drawer instead of routing.
const MOBILE_NAV_TABS_FULL = [
  { kind: 'route', path: '/today-session', icon: '📋', label: 'Today' },
  { kind: 'route', path: '/roster', icon: '👥', label: 'Roster' },
  { kind: 'route', path: '/chat', icon: '🧠', label: 'AI Coach' },
  { kind: 'route', path: '/calendar', icon: '📅', label: 'Calendar' },
  { kind: 'drawer', icon: '☰', label: 'More' },
];

const MOBILE_NAV_TABS_SIMPLIFIED = [
  { kind: 'route', path: '/today-session', icon: '📋', label: 'Today' },
  { kind: 'route', path: '/roster', icon: '👥', label: 'Roster' },
  { kind: 'route', path: '/chat', icon: '🧠', label: 'AI Coach' },
  { kind: 'route', path: '/team-manager', icon: '🏁', label: 'Results' },
  { kind: 'drawer', icon: '☰', label: 'More' },
];

const ESSENTIALS_BLOCKED_PATHS = new Set([
  // Keep /training available in simplified mode because Today, Dashboard and
  // Calendar CTAs route there as the primary "build a session" action.
  '/training-blocks',
  '/tactics-board',
  '/film-room',
  '/scout-library',
  '/admin',
]);

const mobileTabSx = ({ isActive, isButton }) => ({
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
  textDecoration: 'none', cursor: 'pointer', minWidth: '56px',
  ...(isButton && { background: 'none', border: 'none', padding: 0, font: 'inherit' }),
  color: isActive ? 'var(--accent)' : 'var(--text-muted)',
  fontSize: '18px',
  '& span': { fontSize: '12px', fontWeight: isActive ? 600 : 400 }
});

function buildTheme(savedTheme = 'dark', savedFont = 'Segoe UI', savedSize = 'M') {
  var baseFontSize = FONT_SIZE_MAP[savedSize] || 15;
  var isDark = savedTheme !== 'light';

  return createTheme({
    palette: isDark ? {
      mode: 'dark',
      primary: { main: '#5ed6c5' },
      secondary: { main: '#7bbfb4' },
      background: { default: '#0f1d1c', paper: '#1a302d' },
    } : {
      mode: 'light',
      primary: { main: '#087f73' },
      secondary: { main: '#176c64' },
      background: { default: '#eef5f4', paper: '#ffffff' },
    },
    typography: {
      fontFamily: savedFont + ', "Segoe UI", Aptos, Calibri, sans-serif',
      fontSize: baseFontSize,
    },
    shape: { borderRadius: 10 },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderColor: 'var(--border)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 10, fontWeight: 600 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
    },
  });
}

function applyThemeVariables(themeName, savedFont, savedSize) {
  const isDark = themeName !== 'light';
  const root = document.documentElement;
  const values = isDark ? {
    '--page': '#0f1d1c',
    '--surface': '#1a302d',
    '--surface-raised': '#142725',
    '--input': '#21403b',
    '--border': '#31564f',
    '--border-strong': '#648d83',
    '--text': '#e6f4f0',
    '--text-muted': '#a5c4bc',
    '--accent': '#5ed6c5',
    '--accent-hover': '#7be2d2',
    '--success': '#79dba8',
    '--warning': '#f5c451',
    '--danger': '#f28b82',
    '--text-on-accent': '#08241f',
    '--shadow': '0 10px 28px rgba(0, 0, 0, 0.28)',
    '--surface-soft': '#1d3834',
    '--font-family': `${savedFont}, "Segoe UI", Aptos, Calibri, sans-serif`,
  } : {
    '--page': '#eef5f4',
    '--surface': '#ffffff',
    '--surface-raised': '#f8fbfa',
    '--input': '#e6efed',
    '--border': '#c8d8d4',
    '--border-strong': '#68827a',
    '--text': '#192725',
    '--text-muted': '#58706a',
    '--accent': '#087f73',
    '--accent-hover': '#06655c',
    '--success': '#147a50',
    '--warning': '#9a6500',
    '--danger': '#b42318',
    '--text-on-accent': '#ffffff',
    '--shadow': '0 1px 2px rgba(16, 40, 36, 0.08), 0 8px 22px rgba(16, 40, 36, 0.08)',
    '--surface-soft': '#eef5f4',
    '--font-family': `${savedFont}, "Segoe UI", Aptos, Calibri, sans-serif`,
  };
  Object.entries(values).forEach(([key, value]) => root.style.setProperty(key, value));
  const aliases = {
    '--bg-primary': 'var(--page)',
    '--bg-card': 'var(--surface)',
    '--bg-input': 'var(--input)',
    '--border-color': 'var(--border)',
    '--border-input': 'var(--border-strong)',
    '--text-primary': 'var(--text)',
    '--text-secondary': 'var(--text-muted)',
    '--text-dim': 'var(--text-muted)',
    '--bg-hover': 'var(--surface-soft)',
    '--bg-muted': 'var(--surface-soft)',
    '--bg-tooltip': 'var(--surface-raised)',
    '--bg-sidebar': 'var(--surface-raised)',
    '--accent-dim': 'var(--accent-soft)',
  };
  Object.entries(aliases).forEach(([key, value]) => root.style.setProperty(key, value));
  root.style.setProperty('--accent-soft', isDark ? 'rgba(94, 214, 197, 0.14)' : 'rgba(8, 127, 115, 0.10)');
  root.style.setProperty('--app-bar-height', '64px');
  root.style.setProperty('--dark-mode-invert', isDark ? '1' : '0');
  root.setAttribute('data-theme', isDark ? 'dark' : 'light');
  root.style.setProperty('--font-size-base', `${FONT_SIZE_MAP[savedSize] || FONT_SIZE_MAP.M}px`);
}

function LoginPage({ onLogin }) {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resending, setResending] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [pendingMfaUser, setPendingMfaUser] = useState(null);
  const [oauthProviders, setOauthProviders] = useState({ google: true, microsoft: true });

  // Handle OAuth redirect (user payload in URL; auth cookie is set by backend)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const userStr = params.get('user');
    const oauth = params.get('oauth');
    const authError = params.get('error');

    if (authError) {
      sessionStorage.removeItem('pendingParentInvite');
      localStorage.removeItem('pendingParentInvite');
      setError('Social login failed. Please try again.');
      window.history.replaceState({}, document.title, '/login');
    }

    if (oauth === '1' || userStr) {
      authAPI.getProfile().then((res) => {
        const user = res.data?.data || res.data;
        if (!user) throw new Error('Missing profile');
        localStorage.removeItem('mustChangePassword');
        localStorage.setItem('user', JSON.stringify(user));
        window.history.replaceState({}, document.title, '/login');

        // Check for pending parent invite from OAuth flow
        const pendingInviteRaw = sessionStorage.getItem('pendingParentInvite') || localStorage.getItem('pendingParentInvite');
        if (pendingInviteRaw) {
          let pendingInvite = '';
          let storedAt = 0;
          try {
            const parsed = JSON.parse(pendingInviteRaw);
            pendingInvite = String(parsed?.token || '');
            storedAt = Number(parsed?.storedAt || 0);
          } catch {
            pendingInvite = String(pendingInviteRaw || '');
          }
          const isFresh = !storedAt || (Date.now() - storedAt) <= (7 * 24 * 60 * 60 * 1000);
          sessionStorage.removeItem('pendingParentInvite');
          localStorage.removeItem('pendingParentInvite');
          // Validate token format (alphanumeric/hex only)
          if (isFresh && /^[a-f0-9]+$/i.test(pendingInvite)) {
            createLoginGeneration();
            window.location.href = '/parent-invite/' + pendingInvite;
            return;
          }
        }
        onLogin(user, { fresh: true });
      }).catch(() => {
        setError('Social login failed. Please try again.');
        window.history.replaceState({}, document.title, '/login');
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Show success messages passed via navigation state (e.g., after registration/reset)
  useEffect(() => {
    const msg = location.state?.message;
    if (msg) {
      setSuccess(msg);
      const t = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(t);
    }
  }, [location.state]);

  useEffect(() => {
    let active = true;
    authAPI.getProviders()
      .then((res) => {
        if (!active) return;
        const data = res?.data || {};
        setOauthProviders({
          google: data.google !== false,
          microsoft: data.microsoft !== false,
        });
      })
      .catch(() => {
        if (!active) return;
        setOauthProviders({ google: true, microsoft: true });
      });
    return () => { active = false; };
  }, []);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const finalizeLogin = (_token, user, mustChangePassword) => {
    if (mustChangePassword) {
      localStorage.setItem('mustChangePassword', 'true');
    } else {
      localStorage.removeItem('mustChangePassword');
    }
    localStorage.setItem('user', JSON.stringify(user));

    const inviteParams = new URLSearchParams(window.location.search);
    const inviteToken = inviteParams.get('inviteToken');
    if (inviteToken) {
      createLoginGeneration();
      window.location.href = '/parent-invite/' + encodeURIComponent(inviteToken);
      return;
    }

    // Only the club invite page is an allowed post-login redirect (no open redirect).
    const redirect = inviteParams.get('redirect') || '';
    if (/^\/invite\?token=[A-Za-z0-9_-]+$/.test(redirect)) {
      createLoginGeneration();
      window.location.href = redirect;
      return;
    }

    onLogin(user, { fresh: true });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNeedsVerify(false);
    setMfaRequired(false);
    setMfaToken('');
    setMfaCode('');
    setRecoveryCode('');
    setUseRecovery(false);
    setPendingMfaUser(null);
    try {
      const res = await authAPI.login({ email, password });
      const { token, user, mustChangePassword, mfaRequired: loginNeedsMfa, mfaToken: loginMfaToken } = res.data;
      if (loginNeedsMfa && loginMfaToken) {
        setMfaRequired(true);
        setMfaToken(loginMfaToken);
        setPendingMfaUser(user || null);
        setLoading(false);
        return;
      }
      finalizeLogin(token, user, mustChangePassword);
    } catch (err) {
      if (err.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerify(true);
        setError('Please verify your email before signing in. Check your inbox for the verification link.');
      } else {
        setError(err.response?.data?.error || 'Login failed. Check your credentials.');
      }
    }
    setLoading(false);
  };

  const handleMfaLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const code = useRecovery ? '' : mfaCode.trim();
      const recovery = useRecovery ? recoveryCode.trim() : '';
      const res = await authAPI.mfaVerifyLogin(mfaToken, code || undefined, recovery || undefined);
      const { token, user, mustChangePassword } = res.data;
      finalizeLogin(token, user, mustChangePassword);
    } catch (err) {
      const msg = err.response?.data?.error || 'MFA verification failed. Please try again.';
      if (/expired MFA token|sign in again|invalid or expired/i.test(msg)) {
        setMfaRequired(false);
        setMfaToken('');
        setMfaCode('');
        setRecoveryCode('');
        setUseRecovery(false);
      }
      setError(msg);
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    if (!email) { setError('Enter your email above, then resend.'); return; }
    setResending(true);
    setError('');
    try {
      await authAPI.resendVerification(email);
      setSuccess('Verification email sent. If it does not arrive within 5 minutes, check your junk or spam folder.');
      setNeedsVerify(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not resend verification email. Please try again.');
    }
    setResending(false);
  };

  return (
    <div style={{ minHeight: 'var(--app-height)', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ background: 'var(--bg-card)', padding: window.innerWidth < 480 ? '24px' : '40px', borderRadius: '16px', width: '90%', maxWidth: '420px', border: '1px solid var(--border-color)' }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <BrandLogo height={54} showBeta />
          <p style={{ color: 'var(--text-dim)', fontSize: '13px', margin: '10px 0 0' }}>Sports Performance Platform</p>
        </div>
        {success && <div style={{ background: '#134E4A', border: '1px solid #0D9488', color: '#5EEAD4', padding: '10px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>{success}</div>}
        {error && <div role="alert" aria-live="assertive" aria-atomic="true" style={{ background: '#450a0a', border: '1px solid #dc2626', color: '#fca5a5', padding: '10px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>}
        {mfaRequired ? (
          <form onSubmit={handleMfaLogin}>
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>
              Enter your authenticator code for {pendingMfaUser?.email || email}.
            </div>
            {!useRecovery ? (
              <>
                <label htmlFor="mfa-code" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '4px' }}>6-digit authenticator code</label>
                <input id="mfa-code" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} aria-label="6-digit authenticator code" placeholder="000000" value={mfaCode} onChange={e => setMfaCode(e.target.value)} style={{ width: '100%', padding: '12px', marginBottom: '10px', borderRadius: '8px', border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '16px', boxSizing: 'border-box' }} />
                <button type="button" onClick={() => { setUseRecovery(true); setMfaCode(''); }} style={{ width: '100%', padding: '10px', marginBottom: '12px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-input)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  Can't access your authenticator? Use a recovery code
                </button>
              </>
            ) : (
              <>
                <label htmlFor="recovery-code" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '4px' }}>Recovery code</label>
                <input id="recovery-code" type="text" autoComplete="off" spellCheck={false} aria-label="Recovery code" placeholder="XXXXXX-XXXXXX" value={recoveryCode} onChange={e => setRecoveryCode(e.target.value)} style={{ width: '100%', padding: '12px', marginBottom: '10px', borderRadius: '8px', border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '16px', boxSizing: 'border-box' }} />
                <button type="button" onClick={() => { setUseRecovery(false); setRecoveryCode(''); }} style={{ width: '100%', padding: '10px', marginBottom: '12px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-input)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  Back to authenticator code
                </button>
              </>
            )}
            <button type="submit" disabled={loading || (!useRecovery && !mfaCode.trim()) || (useRecovery && !recoveryCode.trim())} aria-busy={loading} style={{ width: '100%', padding: '12px', background: loading ? 'var(--border-input)' : 'var(--accent)', color: 'var(--text-on-accent)', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Verifying...' : 'Verify MFA'}
            </button>
            <button type="button" onClick={() => { setMfaRequired(false); setMfaToken(''); setMfaCode(''); setRecoveryCode(''); setUseRecovery(false); }} style={{ width: '100%', padding: '10px', marginTop: '10px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-input)', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
              Back to password login
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin}>
            <input type="email" aria-label="Email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required style={{ width: '100%', padding: '12px', marginBottom: '12px', borderRadius: '8px', border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '16px', boxSizing: 'border-box' }} />
            <input type="password" aria-label="Password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required style={{ width: '100%', padding: '12px', marginBottom: '16px', borderRadius: '8px', border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '16px', boxSizing: 'border-box' }} />
            <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px', background: loading ? 'var(--border-input)' : 'var(--accent)', color: 'var(--text-on-accent)', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {needsVerify && !mfaRequired && (
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={resending}
            style={{
              width: '100%', padding: '10px', marginTop: '12px', background: 'transparent',
              color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: '8px',
              fontSize: '14px', fontWeight: '600', cursor: resending ? 'not-allowed' : 'pointer',
            }}>
            {resending ? 'Sending…' : 'Resend verification email'}
          </button>
        )}

        {!mfaRequired && (oauthProviders.google || oauthProviders.microsoft) && (
          <>
            <div style={{ position: 'relative', textAlign: 'center', margin: '20px 0 16px' }}>
              <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: 'var(--border-input)' }} />
              <span style={{ position: 'relative', background: 'var(--bg-card)', padding: '0 12px', color: 'var(--text-dim)', fontSize: '12px' }}>or continue with</span>
            </div>
            {oauthProviders.google && (
              <button
                onClick={() => {
                  const itp = new URLSearchParams(window.location.search).get('inviteToken');
                  if (itp) {
                    sessionStorage.setItem('pendingParentInvite', JSON.stringify({ token: itp, storedAt: Date.now() }));
                  } else {
                    sessionStorage.removeItem('pendingParentInvite');
                    localStorage.removeItem('pendingParentInvite');
                  }
                  window.location.href = API_URL.replace(/\/api$/, '') + '/api/auth/google';
                }}
                style={{
                  width: '100%', padding: '12px', background: 'var(--bg-input)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-input)', borderRadius: '8px', fontSize: '14px',
                  fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '10px'
                }}>
                <svg aria-hidden="true" width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                Sign in with Google
              </button>
            )}
            {oauthProviders.microsoft && (
              <button
                onClick={() => {
                  const itp = new URLSearchParams(window.location.search).get('inviteToken');
                  if (itp) {
                    sessionStorage.setItem('pendingParentInvite', JSON.stringify({ token: itp, storedAt: Date.now() }));
                  } else {
                    sessionStorage.removeItem('pendingParentInvite');
                    localStorage.removeItem('pendingParentInvite');
                  }
                  window.location.href = API_URL.replace(/\/api$/, '') + '/api/auth/microsoft';
                }}
                style={{
                  width: '100%', padding: '12px', background: 'var(--bg-input)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-input)', borderRadius: '8px', fontSize: '14px',
                  fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '10px', marginTop: oauthProviders.google ? '8px' : 0
                }}>
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 23 23">
                  <path fill="#f35325" d="M1 1h10v10H1z"/>
                  <path fill="#81bc06" d="M12 1h10v10H12z"/>
                  <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                  <path fill="#ffba08" d="M12 12h10v10H12z"/>
                </svg>
                Sign in with Microsoft
              </button>
            )}
          </>
        )}

        <div style={{ color: 'var(--text-dim)', fontSize: '13px', textAlign: 'center', marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Link to="/forgot-password" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '13px' }}>Forgot Password?</Link>
          <span style={{ color: 'var(--text-dim)' }}>·</span>
          <Link to="/about" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '13px' }}>About</Link>
          <span style={{ color: 'var(--text-dim)' }}>·</span>
          <Link to="/privacy-policy" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '13px' }}>Privacy</Link>
          <span style={{ color: 'var(--text-dim)' }}>·</span>
          <Link to="/terms-of-service" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '13px' }}>Terms</Link>
        </div>
      </div>
    </div>
  );
}

function MainLayout({ onLogout, onVisualSettingsChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);
  const [user, setLocalUser] = useState(() => getStoredUser());
  const isAdmin = user.role === 'admin' || user.role === 'owner' || user.role === 'Sub-Admin' || user.role === 'club_admin';
  const userPlan = user.effectivePlan || user.subscription?.plan || 'free';
  const featureSet = useMemo(() => new Set(user.features || []), [user.features]);
  const canUse = useCallback((feature) => (
    user.role === 'admin'
    || featureSet.has(feature)
    || (['pro', 'club'].includes(userPlan))
    || (userPlan === 'essentials' && [
      'ai_chat', 'squad_roster', 'calendar', 'today_session', 'match_results',
      'parent_messaging', 'voice_notes', 'training_plans', 'document_upload', 'rag_search',
      'club_chat'
    ].includes(feature))
  ), [featureSet, user.role, userPlan]);
  const isProPlus = canUse('film_room');
  const canUseClubChat = canUse('club_chat');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [fbPos, setFbPos] = useState(() => {
    try { return clampFeedbackPosition(JSON.parse(localStorage.getItem('eliteedge_fb_pos') || 'null')); } catch { return null; }
  });
  const fbDragging = useRef(false);
  const fbOffset = useRef({ x: 0, y: 0 });
  const fbRef = useRef(null);
  const mobileNavRef = useRef(null);
  const handleFbPointerDown = (e) => {
    fbDragging.current = false;
    fbOffset.current = { x: e.clientX - (fbPos?.x ?? (window.innerWidth - 68)), y: e.clientY - (fbPos?.y ?? (isMobile ? window.innerHeight - 176 : window.innerHeight - 120)) };
    const onMove = (me) => {
      fbDragging.current = true;
      const next = clampFeedbackPosition({
        x: me.clientX - fbOffset.current.x,
        y: me.clientY - fbOffset.current.y,
      }, getFeedbackBottomInset());
      setFbPos(next);
      localStorage.setItem('eliteedge_fb_pos', JSON.stringify(next));
    };
    const onUp = () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const { teams, activeTeam, switchTeam, refreshTeams } = useTeam();
  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeTeam?._id, location.search]);
  // teamManagerOpen state removed in row 91 — Team Manager is now a route (/team-manager)
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showJourney, setShowJourney] = useState(false);
  const [experienceMode, setExperienceMode] = useState(() => (
    user.experienceMode
    || user.preferences?.experienceMode
    || localStorage.getItem('eliteedge_experience_mode')
    || 'full'
  ));
  const experienceModeRef = useRef(experienceMode);
  const experienceChangedRef = useRef(false);
  const [chatUnread, setChatUnread] = useState(0);
  // Wave 1 #2 (row 99): per-channel breakdown so hovering the badge shows
  // which channels have unread messages. Eric asked for this directly.
  const [chatUnreadChannels, setChatUnreadChannels] = useState([]);
  const [memberMessageUnread, setMemberMessageUnread] = useState({
    unreadCount: 0,
    directUnreadCount: 0,
    channelUnreadCount: 0,
  });
  const [hasParentAccess, setHasParentAccess] = useState(false);
  const [parentAccessChecked, setParentAccessChecked] = useState(false);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const getFeedbackBottomInset = useCallback(() => (
    isMobile ? Math.max(68, mobileNavRef.current?.getBoundingClientRect().height || 0) : 0
  ), [isMobile]);
  useEffect(() => {
    const keepFeedbackVisible = () => {
      setFbPos(previous => {
        const next = clampFeedbackPosition(previous, getFeedbackBottomInset());
        if (!next || (next.x === previous?.x && next.y === previous?.y)) return previous;
        localStorage.setItem('eliteedge_fb_pos', JSON.stringify(next));
        return next;
      });
    };
    keepFeedbackVisible();
    window.addEventListener('resize', keepFeedbackVisible);
    window.addEventListener('orientationchange', keepFeedbackVisible);
    return () => {
      window.removeEventListener('resize', keepFeedbackVisible);
      window.removeEventListener('orientationchange', keepFeedbackVisible);
    };
  }, [getFeedbackBottomInset]);
  // Dashboard is the coach post-login and fallback home on every viewport.
  // Today's Practice remains directly available from the mobile navigation.
  const isEssentialsExperience = experienceMode !== 'full';
  const locale = user.locale || detectDeviceLocale().locale;
  const country = user.country || detectDeviceLocale().country;
  const terms = termsForLocale(locale);
  const mobileNavTabs = (isEssentialsExperience ? MOBILE_NAV_TABS_SIMPLIFIED : MOBILE_NAV_TABS_FULL).map((tab) => {
    if (tab.path === '/today-session') return { ...tab, label: 'Today' };
    if (tab.path === '/roster') return { ...tab, label: termsForLocale(locale).Roster };
    if (tab.path === '/team-manager') return { ...tab, label: `${termsForLocale(locale).Match}s` };
    return tab;
  });
  const coachHome = '/dashboard';
  const guardRoute = useCallback((path, feature, element) => {
    if (feature && !canUse(feature)) return <Navigate to={coachHome} replace />;
    if (!isEssentialsExperience) return element;
    if (!ESSENTIALS_BLOCKED_PATHS.has(path)) return element;
    return <Navigate to={coachHome} replace />;
  }, [canUse, isEssentialsExperience, coachHome]);

  const setExperience = useCallback((mode) => {
    const nextMode = mode === 'full' ? 'full' : 'essentials';
    experienceChangedRef.current = true;
    experienceModeRef.current = nextMode;
    setExperienceMode(nextMode);
    localStorage.setItem('eliteedge_experience_mode', nextMode);
    setLocalUser((previous) => {
      const nextUser = {
        ...previous,
        experienceMode: nextMode,
        preferences: { ...(previous.preferences || {}), experienceMode: nextMode },
      };
      localStorage.setItem('user', JSON.stringify(nextUser));
      return nextUser;
    });
  }, []);

  useEffect(() => {
    let active = true;
    authAPI.getProfile().then((res) => {
      if (!active) return;
      const profile = res.data?.data || res.data;
      if (!profile) return;
      const detected = detectDeviceLocale();
      const merged = {
        ...user,
        ...profile,
        locale: profile.locale || detected.locale,
        country: profile.country || detected.country,
      };
      const resolvedExperience = experienceChangedRef.current
        ? experienceModeRef.current
        : (profile.experienceMode || profile.preferences?.experienceMode || experienceModeRef.current);
      merged.experienceMode = resolvedExperience;
      merged.preferences = {
        ...(profile.preferences || {}),
        experienceMode: resolvedExperience,
      };
      setLocalUser(merged);
      experienceModeRef.current = resolvedExperience;
      setExperienceMode(resolvedExperience);
      localStorage.setItem('user', JSON.stringify(merged));
      if (!profile.locale || !profile.country) {
        api.put('/ai/settings', { locale: detected.locale, country: detected.country }).catch(() => {});
      }
    }).catch(() => {});
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Wave 1 #5 (row 102): GuidedTour dispatches this when a step's target lives
  // inside the collapsed mobile drawer. Open the drawer so the highlight can resolve.
  useEffect(() => {
    const openDrawerForTour = () => { if (isMobile) setMobileOpen(true); };
    window.addEventListener('tour-open-drawer', openDrawerForTour);
    return () => window.removeEventListener('tour-open-drawer', openDrawerForTour);
  }, [isMobile]);

  // Helper: persist onboarding update to server + localStorage
  const updateOnboarding = (action, metadata = {}) => {
    const updates = {};
    const now = new Date().toISOString();
    if (action === 'completeTour') {
      updates.tourCompleted = true;
      updates.tourCompletedAt = now;
      if (metadata.version) updates.tourVersion = metadata.version;
      if (metadata.experience) updates.tourExperience = metadata.experience;
    } else if (action === 'completeJourney') {
      updates.journeyCompleted = true;
      updates.journeyCompletedAt = now;
      if (metadata.version) updates.journeyVersion = metadata.version;
      if (metadata.experience) updates.journeyExperience = metadata.experience;
    } else if (action === 'viewJourney') {
      if (metadata.version) updates.journeyVersion = metadata.version;
      if (metadata.experience) updates.journeyExperience = metadata.experience;
    }
    // Optimistic: update local state + localStorage immediately
    setLocalUser(function (prev) {
      var updatedUser = { ...prev, onboarding: { ...(prev.onboarding || {}), ...updates } };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    });
    // Background sync to server (non-blocking)
    authAPI.updateOnboarding(action, metadata).catch(function (err) {
      console.error('[Onboarding] Failed to sync:', err.message || err);
    });
  };

  useEffect(() => {
    mergeServerProgress(user.onboarding?.journeySteps);
    flushPendingJourneySteps();
  }, [user.onboarding?.journeySteps]);

  useEffect(() => {
    const flushOnReconnect = () => { flushPendingJourneySteps(); };
    window.addEventListener('online', flushOnReconnect);
    return () => window.removeEventListener('online', flushOnReconnect);
  }, []);

  // Tour + Journey — only show on first visit, never after dismissal (server-side persisted)
  useEffect(() => {
    if (location.state?.startTour) {
      setShowTour(true);
      window.history.replaceState({}, document.title);
    } else if (location.state?.showJourney) {
      setShowJourney(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // One-time tour check on mount — use server state, with localStorage migration
  useEffect(() => {
    var migrated = false;
    // Migrate old localStorage flags to server (one-time)
    var oldTourDone = localStorage.getItem('eliteedge_tour_completed') === 'true' || localStorage.getItem('eliteedge_tour_dismissed') === 'true';
    var oldJourneyDone = localStorage.getItem('eliteedge_journey_seen') === 'true';

    if (oldTourDone) {
      // Old flags exist — migrate to server (retries until old keys are cleared on success)
      migrated = true;
      if (!user.onboarding?.tourCompleted) {
        var tourUpdate = { tourCompleted: true, tourCompletedAt: new Date().toISOString() };
        var u1 = { ...user, onboarding: { ...(user.onboarding || {}), ...tourUpdate } };
        if (oldJourneyDone && !user.onboarding?.journeyCompleted) {
          u1.onboarding.journeyCompleted = true;
          u1.onboarding.journeyCompletedAt = new Date().toISOString();
        }
        setLocalUser(u1);
        localStorage.setItem('user', JSON.stringify(u1));
      }
      // Sync to server, only clear old keys on success
      Promise.all([
        authAPI.updateOnboarding('completeTour'),
        oldJourneyDone ? authAPI.updateOnboarding('completeJourney') : Promise.resolve()
      ]).then(function () {
        localStorage.removeItem('eliteedge_tour_completed');
        localStorage.removeItem('eliteedge_tour_dismissed');
        localStorage.removeItem('eliteedge_journey_seen');
      }).catch(function () {
        // Server sync failed — keep old localStorage keys for retry on next mount
      });
    } else if (oldJourneyDone) {
      migrated = true;
      if (!user.onboarding?.journeyCompleted) {
        var u2 = { ...user, onboarding: { ...(user.onboarding || {}), journeyCompleted: true, journeyCompletedAt: new Date().toISOString() } };
        setLocalUser(u2);
        localStorage.setItem('user', JSON.stringify(u2));
      }
      authAPI.updateOnboarding('completeJourney').then(function () {
        localStorage.removeItem('eliteedge_journey_seen');
      }).catch(function () {});
    } else if (!user.onboarding) {
      // Pre-deploy cached user without onboarding field — refresh from server before deciding
      authAPI.getProfile().then(function (res) {
        var serverUser = res.data?.data || res.data;
        if (serverUser && serverUser.onboarding) {
          var refreshed = { ...user, onboarding: serverUser.onboarding };
          setLocalUser(refreshed);
          localStorage.setItem('user', JSON.stringify(refreshed));
          if (!serverUser.onboarding.tourCompleted) {
            setShowTour(true);
          }
        } else {
          setShowTour(true);
        }
      }).catch(function () {
        setShowTour(true);
      });
      return;
    }

    // Show tour only if server says not completed AND no migration just happened
    if (!migrated && !user.onboarding?.tourCompleted) {
      setShowTour(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Show journey ONCE after tour is done (not on every render)
  const journeyTriggeredRef = React.useRef(false);
  useEffect(() => {
    journeyTriggeredRef.current = false;
  }, [isEssentialsExperience]);

  useEffect(() => {
    const currentJourneyVersion = getJourneyConfig({
      experienceMode: isEssentialsExperience ? 'essentials' : 'full',
      terms,
    }).version;
    const journeyNotIntroduced = isEssentialsExperience
      ? user.onboarding?.journeyVersion !== currentJourneyVersion
      : !user.onboarding?.journeyCompleted;
    if (!showTour && user.onboarding?.tourCompleted && journeyNotIntroduced && !journeyTriggeredRef.current) {
      journeyTriggeredRef.current = true;
      setShowJourney(true);
    }
  }, [
    showTour,
    isEssentialsExperience,
    terms,
    user.onboarding?.tourCompleted,
    user.onboarding?.journeyCompleted,
    user.onboarding?.journeyVersion,
  ]);

  // Auto-detect journey step completion based on page visits (FU6: slug-based)
  useEffect(() => {
    if (isEssentialsExperience) return;
    var stepMap = {
      '/roster': ['roster'],
      '/training': ['training'],
      '/tactics-board': ['drill', 'tactics'],
      '/chat': ['chat'],
      '/calendar': ['calendar'],
      '/film-room': ['film'],
    };
    var steps = stepMap[location.pathname];
    if (steps) markJourneyStepComplete(steps);
  }, [location.pathname, isEssentialsExperience]);

  // Step 'team': auto-complete when a team exists
  useEffect(() => {
    if (isEssentialsExperience && activeTeam) completeJourneyStep('team');
  }, [activeTeam, isEssentialsExperience]);

  // Step 'match': auto-complete when Team Manager page is visited
  useEffect(() => {
    if (!isEssentialsExperience && location.pathname === '/team-manager') {
      markJourneyStepComplete('match');
    }
  }, [location.pathname, isEssentialsExperience]);

  // Poll for Club Chat unread count for the ACTIVE team's club (every 30s) +
  // refetch on nav to /club-chat, on active-team switch, and on
  // 'chat-unread-refresh' events (dispatched by ClubChat when the user reads a
  // channel). Wave 1 #2 (row 99): request-version guard prevents a slow
  // earlier poll from clobbering a fresh post-read count.
  const unreadReqRef = useRef(0);
  const activeClubId = activeTeam?.clubId?._id || activeTeam?.clubId || '';
  useEffect(() => {
    if (!canUseClubChat) return;
    if (!activeClubId) {
      setChatUnread(0);
      setChatUnreadChannels([]);
      return;
    }
    // Clear stale state on every club switch so the badge never shows the
    // previous club's count during the window before the new fetch resolves
    // (Wave 1 #2 round-3 GPT-5.4 P2).
    setChatUnread(0);
    setChatUnreadChannels([]);
    const fetchUnread = () => {
      const myReq = ++unreadReqRef.current;
      api.get(`/chat/unread?clubId=${encodeURIComponent(activeClubId)}`).then(r => {
        if (myReq !== unreadReqRef.current) return; // stale; a newer request fired
        const payload = r.data?.data || {};
        setChatUnread(payload.total || 0);
        setChatUnreadChannels(Array.isArray(payload.channels) ? payload.channels : []);
      }).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    const onRefresh = () => fetchUnread();
    window.addEventListener('chat-unread-refresh', onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('chat-unread-refresh', onRefresh);
    };
  }, [canUseClubChat, activeClubId]);

  // Wave 1 #2: when the user lands on /club-chat, wait a moment for the
  // messages route to upsert lastReadAt server-side, then refetch so the
  // global badge clears without waiting up to 30s for the interval.
  useEffect(() => {
    if (!canUseClubChat) return;
    if (location.pathname !== '/club-chat') return;
    const t = setTimeout(() => {
      try { window.dispatchEvent(new CustomEvent('chat-unread-refresh')); } catch {}
    }, 2000);
    return () => clearTimeout(t);
  }, [location.pathname, canUseClubChat]);

  useEffect(() => {
    if (!['coach', 'analyst', 'club_admin', 'admin'].includes(user.role)) return undefined;
    const fetchMemberMessageUnread = () => {
      parentMessagesAPI.getUnreadCount()
        .then((response) => {
          const counts = response.data?.data || {};
          setMemberMessageUnread({
            unreadCount: counts.unreadCount || 0,
            directUnreadCount: counts.directUnreadCount || 0,
            channelUnreadCount: counts.channelUnreadCount || 0,
            byClub: Array.isArray(counts.byClub) ? counts.byClub : [],
          });
        })
        .catch((err) => console.error('Failed to refresh member message unread counts:', err));
    };
    fetchMemberMessageUnread();
    const interval = window.setInterval(fetchMemberMessageUnread, 15000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchMemberMessageUnread();
    };
    window.addEventListener('focus', fetchMemberMessageUnread);
    window.addEventListener('member-message-unread-refresh', fetchMemberMessageUnread);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', fetchMemberMessageUnread);
      window.removeEventListener('member-message-unread-refresh', fetchMemberMessageUnread);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user.role]);

  // Check if coach has parent access (via ParentChildLink)
  useEffect(() => {
    if (user.role !== 'parent') {
      parentAPI.hasAccess().then(res => {
        setHasParentAccess(res.data?.data?.hasAccess || false);
      }).catch(() => {}).finally(() => setParentAccessChecked(true));
    } else {
      setParentAccessChecked(true);
    }
  }, [user.role]);

  const fullNavGroups = [
    { items: [{ text: 'Dashboard', path: '/dashboard', icon: '📊', tourId: 'nav-dashboard' }] },
    { label: 'TRAINING', items: [
      ...(canUse('training_plans') ? [{ text: 'Session Builder', path: '/training', icon: '🏃', tourId: 'nav-training' }] : []),
      ...(canUse('calendar') ? [{ text: 'Calendar', path: '/calendar', icon: '📅', tourId: 'nav-calendar' }] : []),
      ...(canUse('today_session') ? [{ text: terms.todaySession, path: '/today-session', icon: '📋', tourId: 'nav-today-practice' }] : []),
      ...(isProPlus ? [{ text: 'Season Planner', path: '/training-blocks', icon: '📆' }] : []),
      ...(canUse('tactical_animations') ? [{ text: 'Tactics Board', path: '/tactics-board', icon: '🎯', tourId: 'nav-tactics-board' }] : []),
    ]},
    { label: 'TEAM', items: [
      { text: terms.Roster, path: '/roster', icon: '👥', tourId: 'nav-roster' },
      { text: 'Team Manager', path: '/team-manager', icon: '⚙️' },
    ]},
    { label: 'ANALYSIS', items: [
      { text: 'AI Coach Chat', path: '/chat', icon: '🧠', tourId: 'nav-chat' },
      ...(canUse('film_room') ? [{ text: 'Film Room', path: '/film-room', icon: '🎬', tourId: 'nav-film' }] : []),
    ]},
    { label: 'COLLABORATE', items: [
      ...(canUse('club_chat') ? [{ text: 'Club Chat', path: '/club-chat', icon: '💬', tourId: 'nav-club-chat' }] : []),
      { text: 'Member Messages', path: '/parent-messages', icon: '👪' },
      ...(hasParentAccess ? [{ text: 'Member Portal', path: '/parent-portal', icon: '👨‍👩‍👧' }] : []),
    ]},
    { label: 'MORE', items: [
      ...(isProPlus ? [{ text: 'Scout Library', path: '/scout-library', icon: '🔍', tourId: 'nav-scout' }] : []),
      ...(canUse('document_upload') ? [{ text: 'Documents', path: '/documents', icon: '📄', tourId: 'nav-docs' }] : []),
      { text: 'Privacy Policy', path: '/privacy-policy', icon: '🔒' },
      { text: 'Terms of Service', path: '/terms-of-service', icon: '📜' },
      { text: 'Help', path: '/help', icon: '❓', tourId: 'nav-help' },
      { text: 'Your Journey', path: '#journey', icon: '📍', isJourney: true },
      { text: 'About', path: '/about', icon: 'ℹ️', tourId: 'nav-about' },
      ...(isAdmin ? [{ text: 'Admin Dashboard', path: '/admin', icon: '⚙️' }] : []),
    ]},
  ];
  const simplifiedNavGroups = [
    { items: [{ text: terms.todaySession, path: '/today-session', icon: '📋', tourId: 'nav-today-practice' }] },
    { label: 'FIELD ESSENTIALS', items: [
      { text: `${terms.Match} Results & Team`, path: '/team-manager', icon: '🏁' },
      { text: terms.Roster, path: '/roster', icon: '👥', tourId: 'nav-roster' },
      { text: 'AI Coach Chat', path: '/chat', icon: '🧠', tourId: 'nav-chat' },
      { text: 'Session Builder', path: '/training', icon: '🏃', tourId: 'nav-training' },
      { text: 'Calendar', path: '/calendar', icon: '📅', tourId: 'nav-calendar' },
      { text: 'Documents', path: '/documents', icon: '📄', tourId: 'nav-docs' },
      ...(canUseClubChat ? [{ text: 'Club Chat', path: '/club-chat', icon: '💬', tourId: 'nav-club-chat' }] : []),
      { text: 'Member Messages', path: '/parent-messages', icon: '👪' },
      ...(hasParentAccess ? [{ text: 'Member Portal', path: '/parent-portal', icon: '👨‍👩‍👧' }] : []),
      { text: 'Your Journey', path: '#journey', icon: '📍', isJourney: true },
      { text: 'Privacy Policy', path: '/privacy-policy', icon: '🔒' },
      { text: 'Terms of Service', path: '/terms-of-service', icon: '📜' },
      { text: 'Help', path: '/help', icon: '❓', tourId: 'nav-help' },
      { text: 'About', path: '/about', icon: 'ℹ️', tourId: 'nav-about' },
    ]},
  ];
  const navGroups = isEssentialsExperience ? simplifiedNavGroups : fullNavGroups;

  const drawerContent = (
    <>
      {activeTeam ? (
        <Box sx={{ p: 2, borderBottom: '1px solid var(--border-color)', background: 'var(--bg-sidebar)', display: 'flex', gap: 1.25, alignItems: 'center' }}>
          <ClubLogo club={activeTeam.clubId} size="sm" decorative />
          <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'var(--text-dim)', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.5px' }}>Active Team</Typography>
          {activeTeam.clubId?.name && <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', fontSize: '11px', mt: 0.5 }}>{activeTeam.clubId.name}</Typography>}
          <Typography variant="body2" sx={{ color: 'var(--accent)', fontWeight: 600, mt: 0.3 }}>{activeTeam.name}</Typography>
          {activeTeam.ageGroup && <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{activeTeam.ageGroup}{activeTeam.season ? ' \xB7 ' + activeTeam.season : ''}</Typography>}
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 2, borderBottom: '1px solid var(--border-color)', background: 'var(--bg-sidebar)' }}>
          <Typography variant="caption" sx={{ color: '#f59e0b', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.5px' }}>No Team Selected</Typography>
          <Button onClick={() => { setMobileOpen(false); navigate('/team-manager'); }} size="small" sx={{ display: 'block', color: 'var(--accent)', textTransform: 'none', fontSize: '12px', p: 0, mt: 0.5 }}>
            + Create your first team
          </Button>
        </Box>
      )}
      {isMobile && (
        <Box sx={{ p: 2, borderBottom: '1px solid var(--border-color)', background: 'var(--bg-sidebar)' }}>
          <Typography variant="caption" sx={{ color: 'var(--text-dim)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Team</Typography>
          {teams.length > 0 ? (
            <select
              value={activeTeam?._id || ''}
              onChange={(e) => switchTeam(e.target.value)}
              style={{
                width: '100%', marginTop: '4px',
                background: 'var(--bg-sidebar)', color: 'var(--accent)', border: '1px solid var(--border-input)',
                borderRadius: '6px', padding: '6px 8px', fontSize: '13px', fontWeight: 600,
                cursor: 'pointer', outline: 'none'
              }}
            >
              {teams.map(t => (
                <option key={t._id} value={t._id}>
                  {t.clubId?.name ? '[' + t.clubId.name + '] ' : ''}{t.name}{t.ageGroup ? ' (' + t.ageGroup + ')' : ''}
                </option>
              ))}
            </select>
          ) : (
            <Typography variant="caption" sx={{ color: '#f59e0b', fontSize: '12px', display: 'block', mt: 0.5 }}>No teams</Typography>
          )}
          <Button onClick={() => { navigate('/team-manager'); setMobileOpen(false); }} size="small" sx={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'none', p: 0, mt: 0.5 }}>
            {teams.length === 0 ? '+ Create' : 'Manage Teams'}
          </Button>
        </Box>
      )}
      {navGroups.map((group, gi) => (
        <Box key={gi} sx={{ borderBottom: '1px solid var(--border-color)', py: 0.5 }}>
          {group.label && (
            <Typography sx={{ fontSize: '9px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 700, px: 2, pt: 1, pb: 0.5 }}>
              {group.label}
            </Typography>
          )}
          <List disablePadding>
            {group.items.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={item.disabled || item.isJourney ? 'div' : Link}
                  to={item.disabled || item.isJourney ? undefined : item.path}
                  selected={!item.disabled && !item.isJourney && location.pathname === item.path}
                  onClick={() => {
                    if (item.disabled) {
                      alert('Upgrade to Pro to access ' + item.text.replace(' (Pro)', ''));
                      return;
                    }
                    if (item.isJourney) {
                      setShowJourney(true);
                    }
                    if (isMobile) setMobileOpen(false);
                  }}
                  data-tour={item.tourId}
                  sx={{ py: 0.6, '&.Mui-selected': { background: '#2DD4BF15', borderRight: '3px solid #2DD4BF' }, opacity: item.disabled ? 0.5 : 1, cursor: item.isJourney ? 'pointer' : undefined }}
                >
                  <ListItemIcon sx={{ minWidth: 32, fontSize: '14px' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} sx={{ '& .MuiListItemText-primary': { color: item.disabled ? 'var(--text-secondary)' : (location.pathname === item.path ? 'var(--accent)' : 'var(--text-secondary)'), fontSize: '13px' } }} />
                  {item.path === '/club-chat' && chatUnread > 0 && (() => {
                    const nonMuted = (chatUnreadChannels || []).filter(c => !c.muted && c.unreadCount > 0);
                    const tooltipContent = nonMuted.length > 0 ? (
                      <Box sx={{ py: 0.5 }} data-testid="club-chat-unread-tooltip">
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Unread messages:</Typography>
                        {nonMuted.slice(0, 10).map(c => (
                          <Typography key={c.channelId} variant="caption" sx={{ display: 'block' }}>
                            {c.displayName} ({c.unreadCount})
                          </Typography>
                        ))}
                        {nonMuted.length > 10 && (
                          <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>+ {nonMuted.length - 10} more…</Typography>
                        )}
                      </Box>
                    ) : `${chatUnread} unread`;
                    return (
                      <Tooltip title={tooltipContent} arrow enterTouchDelay={50} leaveTouchDelay={4000}>
                        <span
                          data-testid="club-chat-unread-badge"
                          style={{ background: '#ef4444', color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '9px', fontWeight: 700, minWidth: '18px', textAlign: 'center', cursor: 'help' }}
                        >{chatUnread > 99 ? '99+' : chatUnread}</span>
                      </Tooltip>
                    );
                  })()}
                  {item.path === '/parent-messages' && memberMessageUnread.unreadCount > 0 && (
                    <Tooltip
                      title={[
                        `${memberMessageUnread.directUnreadCount} direct · ${memberMessageUnread.channelUnreadCount} team channel${memberMessageUnread.channelUnreadCount === 1 ? '' : 's'}`,
                        ...(memberMessageUnread.byClub || []).map(item => `${item.clubName}: ${item.unreadCount}`),
                      ].join(' · ')}
                      arrow
                    >
                      <span
                        data-testid="member-messages-unread-badge"
                        aria-label={`${memberMessageUnread.unreadCount} unread member messages`}
                        style={{ background: '#ef4444', color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '9px', fontWeight: 700, minWidth: '18px', textAlign: 'center' }}
                      >
                        {memberMessageUnread.unreadCount > 99 ? '99+' : memberMessageUnread.unreadCount}
                      </span>
                    </Tooltip>
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
      {isMobile && (
        <Box sx={{ p: 2, borderTop: '1px solid var(--border-color)', mt: 'auto', pb: 'calc(env(safe-area-inset-bottom, 8px) + 70px)' }}>
          <Button
            onClick={() => { setExperience(isEssentialsExperience ? 'full' : 'essentials'); setMobileOpen(false); }}
            size="small"
            sx={{ color: 'var(--accent)', textTransform: 'none', fontSize: '14px', display: 'block', mb: 1, minHeight: '44px' }}
          >
            {isEssentialsExperience ? '🔓 Switch to Full Experience' : '📋 Use Essentials Experience'}
          </Button>
          <Button onClick={() => { setSettingsOpen(true); setMobileOpen(false); }} size="small" sx={{ color: 'var(--accent)', textTransform: 'none', fontSize: '14px', display: 'block', mb: 1, minHeight: '44px' }}>⚙️ Settings</Button>
          <Button onClick={onLogout} size="small" sx={{ color: '#ef4444', textTransform: 'none', fontSize: '14px', display: 'block', minHeight: '44px' }}>🚪 Logout</Button>
        </Box>
      )}
    </>
  );

  return (
    <LocaleProvider locale={locale} country={country} terminology={user.terminology || user.preferences?.terminology}>
    <Box sx={{ display: 'flex', width: '100%', maxWidth: '100vw' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1, background: 'var(--bg-card)', borderBottom: '1px solid var(--border-color)' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} aria-label="Open navigation menu" aria-expanded={mobileOpen} sx={{ color: 'var(--text-secondary)' }}>
                <MenuIcon />
              </IconButton>
            )}
            <Link to="/dashboard" aria-label="Elite Edge AI beta home" style={{ display: 'inline-flex', textDecoration: 'none' }}>
              <BrandLogo variant={isMobile ? 'icon' : 'wordmark'} height={isMobile ? 30 : 38} showBeta />
            </Link>
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1.5 }}>
                <Typography
                  component="a"
                  href="/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  sx={{ color: 'var(--text-secondary)', fontSize: '12px', textDecoration: 'none', '&:hover': { color: 'var(--accent)' } }}
                >
                  Privacy Policy
                </Typography>
                <Typography sx={{ color: 'var(--text-dim)', fontSize: '12px' }}>·</Typography>
                <Typography
                  component="a"
                  href="/terms-of-service"
                  target="_blank"
                  rel="noreferrer"
                  sx={{ color: 'var(--text-secondary)', fontSize: '12px', textDecoration: 'none', '&:hover': { color: 'var(--accent)' } }}
                >
                  Terms
                </Typography>
              </Box>
            )}
            {isMobile && activeTeam && (
              <span onClick={() => setMobileOpen(true)} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMobileOpen(true); } }} style={{
                marginLeft: '8px', padding: '2px 8px', borderRadius: '12px',
                background: '#2DD4BF22', color: 'var(--accent)', fontSize: '11px',
                fontWeight: 600, cursor: 'pointer', maxWidth: '120px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                display: 'inline-block', verticalAlign: 'middle'
              }}>
                {activeTeam.name.length > 18 ? activeTeam.name.substring(0, 18) + '...' : activeTeam.name}
              </span>
            )}
            {!isMobile && (
              <Box data-tour="team-selector" sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, background: 'var(--bg-input)', borderRadius: '8px', padding: '4px 4px 4px 12px' }}>
                <Typography variant="caption" sx={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Team:</Typography>
                {teams.length > 0 ? (
                  <>
                    <select
                      value={activeTeam?._id || ''}
                      onChange={(e) => switchTeam(e.target.value)}
                      style={{
                        background: 'var(--bg-sidebar)', color: 'var(--accent)', border: '1px solid var(--border-input)',
                        borderRadius: '6px', padding: '4px 8px', fontSize: '13px', fontWeight: 600,
                        cursor: 'pointer', outline: 'none', maxWidth: '200px'
                      }}
                    >
                      {teams.map(t => (
                        <option key={t._id} value={t._id}>
                          {t.clubId?.name ? '[' + t.clubId.name + '] ' : ''}{t.name}{t.ageGroup ? ' (' + t.ageGroup + ')' : ''}
                        </option>
                      ))}
                    </select>
                    {teams.length > 1 && (
                      <Typography variant="caption" sx={{ color: '#4ade80', fontSize: '10px', px: 0.5 }}>
                        {teams.length} teams
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="caption" sx={{ color: '#f59e0b', fontSize: '12px', px: 1 }}>No teams</Typography>
                )}
                <Button onClick={() => { setMobileOpen(false); navigate('/team-manager'); }} size="small" sx={{ color: 'var(--text-secondary)', fontSize: '11px', minWidth: 'auto', textTransform: 'none', px: 1 }}>
                  {teams.length === 0 ? '+ Create' : 'Manage'}
                </Button>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: isMobile ? '12px' : undefined }}>{user.name}</Typography>
            {!isMobile && (
              <>
                <Button onClick={() => setSettingsOpen(true)} data-tour="settings-button" size="small" sx={{ color: 'var(--accent)', minWidth: 'auto' }}>{'\u2699'} Settings</Button>
                <Button onClick={onLogout} size="small" sx={{ color: 'var(--text-dim)' }}>Logout</Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', background: 'var(--bg-card)', borderRight: '1px solid var(--border-color)', mt: '64px', height: 'calc(100% - 64px)', overflowY: 'auto' } }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer variant="permanent" sx={{ width: drawerWidth, flexShrink: 0, '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', background: 'var(--bg-card)', borderRight: '1px solid var(--border-color)', mt: '64px', height: 'calc(100% - 64px)', overflowY: 'auto' } }}>
          {drawerContent}
        </Drawer>
      )}
      <Box component="main" sx={{ flexGrow: 1, p: 0, mt: '64px', ml: isMobile ? 0 : 0, minHeight: 'calc(var(--app-height) - 64px)', background: 'var(--bg-primary)', pb: isMobile ? '70px' : 0, minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
        {localStorage.getItem('mustChangePassword') === 'true' && (
          <Box sx={{ background: '#f59e0b', color: '#000', p: 1.5, textAlign: 'center', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            ⚠️ Your admin has set a temporary password. Please change it in <span onClick={() => setSettingsOpen(true)} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSettingsOpen(true); } }} style={{ color: '#000', textDecoration: 'underline', fontWeight: 700, cursor: 'pointer' }}>Settings</span>.
            <button onClick={() => { localStorage.removeItem('mustChangePassword'); window.location.reload(); }} style={{ background: 'rgba(0,0,0,0.15)', border: 'none', borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: '13px', color: '#000', fontWeight: 600, marginLeft: 8 }}>✕ Dismiss</button>
          </Box>
        )}
        <RouteSuspense>
        <Routes>
          {/* /dashboard renders Dashboard on every viewport. Mobile coaches
              reach it via More drawer; we deliberately do NOT redirect here
              so that explicit navigation lands where the user asked. The
              "no bottom-nav tab is highlighted on /dashboard" is intentional
              — Dashboard is not one of the 5 mobile tabs by design. */}
          <Route path="/dashboard" element={
            user.role === 'parent'
              ? <Navigate to="/parent" replace />
              : <DashboardPage onOpenTeamManager={() => navigate('/team-manager')} />
          } />
          <Route path="/parent" element={user.role === 'parent' ? <ParentDashboard /> : <Navigate to={coachHome} replace />} />
          <Route path="/chat" element={guardRoute('/chat', 'ai_chat', <AICoachChat />)} />
          <Route path="/roster" element={guardRoute('/roster', 'squad_roster', <PlayerRoster />)} />
          {/* R217.0A PR1 follow-up: /players historically rendered legacy PlayerAnalysis
             (admin-preview, unused by coaches). Redirect to Squad Roster so deep-links
             land on the canonical squad surface. */}
          <Route path="/players" element={<Navigate to="/roster" replace />} />
          <Route path="/players/:playerId" element={<PlayerFolder />} />
          <Route path="/team-profile" element={guardRoute('/team-profile', 'radar_customization', <TeamProfile />)} />
          <Route path="/team-manager" element={guardRoute('/team-manager', 'match_results', <TeamManager onTeamChange={refreshTeams} />)} />
          <Route path="/training" element={guardRoute('/training', 'training_plans', <TrainingPlanGenerator />)} />
          <Route path="/calendar" element={guardRoute('/calendar', 'calendar', <TrainingCalendar />)} />
          <Route path="/today-session" element={guardRoute('/today-session', 'today_session', <TodaySession />)} />
          <Route path="/training-blocks" element={guardRoute('/training-blocks', 'season_planner', <TrainingBlocks />)} />
          <Route path="/tactics-board" element={guardRoute('/tactics-board', 'tactical_animations', <TacticsBoard />)} />
          <Route path="/film-room" element={guardRoute('/film-room', 'film_room', <FilmRoom />)} />
          <Route path="/scout-library" element={guardRoute('/scout-library', 'scout_library', <ScoutLibrary />)} />
          <Route path="/documents" element={guardRoute('/documents', 'document_upload', <TeamDocuments />)} />
          <Route path="/club-chat" element={guardRoute('/club-chat', 'club_chat', <ClubChat />)} />
          <Route path="/parent-messages" element={guardRoute('/parent-messages', 'parent_messaging', <ParentMessages />)} />
          <Route path="/parent-messages/:parentUserId/:playerId" element={guardRoute('/parent-messages', 'parent_messaging', <ParentMessages />)} />
          <Route path="/parent-portal" element={
            !parentAccessChecked ? <Box sx={{ p: 4, textAlign: 'center', color: 'var(--text-dim)' }}>Loading...</Box> :
            hasParentAccess ? (
            <Box sx={{ position: 'relative' }}>
              <Link to={coachHome} style={{ textDecoration: 'none' }}>
                <Button 
                  sx={{ position: 'fixed', top: 80, left: 260, zIndex: 1200, background: 'var(--bg-hover)', color: 'var(--accent)', border: '1px solid #2DD4BF33', borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 2, py: 0.5 }}
                >
                  ← Back to Coach View
                </Button>
              </Link>
              <Box sx={{ maxWidth: '900px', margin: '0 auto', p: 2, pt: 6 }}>
                <ParentDashboard isCoachView={true} />
              </Box>
            </Box>
            ) : <Navigate to={coachHome} replace />
          } />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/oauth-consent" element={<OAuthConsentPage />} />
          <Route path="/admin" element={isAdmin ? guardRoute('/admin', null, <AdminDashboard />) : <Navigate to={coachHome} replace />} />
          <Route path="/invite" element={<InviteAcceptPage isLoggedIn user={user} />} />
          <Route path="/parent-invite/:token" element={<ParentInviteAccept />} />
          <Route path="/privacy-policy" element={<LegalDocumentPage title="Privacy Policy" filePath="/PRIVACY_POLICY.md" />} />
          <Route path="/terms-of-service" element={<LegalDocumentPage title="Terms of Service" filePath="/TERMS_OF_SERVICE.md" />} />
          <Route path="/drill-designer" element={<Navigate to="/tactics-board" />} />
          <Route path="/animations" element={<Navigate to="/tactics-board" />} />
          <Route path="*" element={<Navigate to={user.role === 'parent' ? '/parent' : coachHome} replace />} />
        </Routes>
        </RouteSuspense>
        <ErrorBoundary label="Settings">
          <SettingsPanel
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            onLogout={onLogout}
            onVisualSettingsChange={onVisualSettingsChange}
            experienceMode={experienceMode}
            onExperienceModeChange={setExperience}
            onProfileSettingsChange={(updates) => {
              setLocalUser((previous) => {
                const nextUser = {
                  ...previous,
                  ...updates,
                  preferences: {
                    ...(previous.preferences || {}),
                    ...(updates.terminology ? { terminology: updates.terminology } : {}),
                  },
                };
                localStorage.setItem('user', JSON.stringify(nextUser));
                return nextUser;
              });
            }}
          />
        </ErrorBoundary>
        {/* Help button — always visible */}
        {!['/chat', '/club-chat', '/tactics-board'].includes(location.pathname) && (
          <Link to="/help" style={{ position: 'fixed', bottom: isMobile ? 80 : 24, right: 24, width: 44, height: 44, borderRadius: '50%', background: 'var(--accent)', color: 'var(--text-on-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 'bold', textDecoration: 'none', boxShadow: '0 4px 12px rgba(0,229,255,0.3)', zIndex: 1000 }}>?</Link>
        )}
        {/* Feedback button — draggable, right side above help button by default. Drag to reposition. */}
        <button
          ref={fbRef}
          onPointerDown={handleFbPointerDown}
          onClick={() => { if (!fbDragging.current) setFeedbackOpen(true); }}
          title="Send feedback (drag to move)"
          aria-label="Send feedback"
          style={{ position: 'fixed', left: fbPos ? fbPos.x : undefined, top: fbPos ? fbPos.y : undefined, bottom: fbPos ? undefined : (isMobile ? 132 : 76), right: fbPos ? undefined : 24, width: 44, height: 44, borderRadius: '50%', background: 'var(--bg-secondary, #1a1a2e)', color: 'var(--accent)', border: '1.5px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'grab', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', zIndex: 1000, touchAction: 'none', userSelect: 'none' }}
        >✍️</button>
        <React.Suspense fallback={null}>
          <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} currentRoute={location.pathname} />
        </React.Suspense>
        {showTour && (
          <GuidedTour
            experienceMode={isEssentialsExperience ? 'essentials' : 'full'}
            terms={terms}
            canUse={canUse}
            onComplete={({ tourVersion, experienceMode: completedExperience }) => {
              updateOnboarding('completeTour', { version: tourVersion, experience: completedExperience });
              setShowTour(false);
            }}
          />
        )}
        <UserJourney
          open={showJourney}
          experienceMode={isEssentialsExperience ? 'essentials' : 'full'}
          terms={terms}
          onClose={() => {
            const journeyConfig = getJourneyConfig({
              experienceMode: isEssentialsExperience ? 'essentials' : 'full',
              terms,
            });
            const progress = readProgress();
            const allStepsComplete = journeyConfig.steps.every((step) => progress[step.id]);
            const action = isEssentialsExperience && !allStepsComplete
              ? 'viewJourney'
              : 'completeJourney';
            updateOnboarding(action, {
              version: journeyConfig.version,
              experience: journeyConfig.id,
            });
            setShowJourney(false);
          }}
          onOpenTeamManager={() => navigate('/team-manager')}
          onOpenSettings={() => setSettingsOpen(true)}
          serverSteps={user.onboarding?.journeySteps || {}}
        />
        <MobileInstallPrompt isMobile={isMobile} isAuthenticated={Boolean(user)} />
      </Box>
      {isMobile && user && (
        <Box
          ref={mobileNavRef}
          component="nav"
          aria-label="Primary"
          sx={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200,
            background: 'var(--bg-card)', borderTop: '1px solid var(--border-color)',
            display: 'flex', justifyContent: 'space-around', alignItems: 'center',
            padding: '6px 0 env(safe-area-inset-bottom, 6px)',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.2)'
          }}
        >
          {(() => {
            // Compute the active tab once per render. Trim trailing slash so
            // /roster/ ≡ /roster (NavLink's `end` prop does not normalize).
            const currentPath = (location.pathname || '/').replace(/\/+$/, '') || '/';
            return mobileNavTabs.map(tab => {
              if (tab.kind === 'drawer') {
                return (
                  <Box
                    key={tab.label}
                    component="button"
                    type="button"
                    aria-label={tab.label}
                    aria-haspopup="dialog"
                    onClick={() => setMobileOpen(true)}
                    sx={mobileTabSx({ isActive: false, isButton: true })}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </Box>
                );
              }
              const isActive = currentPath === tab.path;
              const tabTarget = (() => {
                if (!isEssentialsExperience || tab.path !== '/team-manager') return tab.path;
                const params = new URLSearchParams({ workspace: 'results' });
                if (activeTeam?._id) params.set('team', activeTeam._id);
                return `/team-manager?${params.toString()}`;
              })();
              return (
                <Link
                  key={tab.path}
                  to={tabTarget}
                  aria-label={tab.label}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={(e) => {
                    setMobileOpen(false);
                    if (!isActive) return;
                    // Active retap = scroll to top, do NOT re-navigate (which
                    // would unmount/remount the page and re-run useEffects).
                    e.preventDefault();
                    // Don't scroll the page underneath an open dialog/drawer.
                    // MUI Drawer/Dialog/Popover all render a `.MuiModal-root`
                    // portal when open and remove it when closed.
                    if (document.querySelector('.MuiModal-root, [role="dialog"], [aria-modal="true"]')) return;
                    try {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } catch {
                      window.scrollTo(0, 0);
                    }
                  }}
                  style={{ textDecoration: 'none' }}
                >
                  <Box sx={mobileTabSx({ isActive, isButton: false })}>
                    {tab.icon}
                    <span>{tab.label}</span>
                  </Box>
                </Link>
              );
            });
          })()}
        </Box>
      )}
    </Box>
    </LocaleProvider>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [loginGeneration, setLoginGeneration] = useState(() => readLoginGeneration());
  const [parentNotificationsOpen, setParentNotificationsOpen] = useState(false);
  const isParentMobile = useMediaQuery('(max-width:899.95px)');
  const [visualPrefs, setVisualPrefs] = useState(() => ({
    theme: localStorage.getItem('eliteedge_theme') || 'dark',
    font: localStorage.getItem('eliteedge_font') || 'Segoe UI',
    fontSize: localStorage.getItem('eliteedge_fontSize') || 'M',
  }));
  const theme = useMemo(
    () => buildTheme(visualPrefs.theme, visualPrefs.font, visualPrefs.fontSize),
    [visualPrefs.theme, visualPrefs.font, visualPrefs.fontSize]
  );
  const handleVisualSettingsChange = useCallback((changes) => {
    setVisualPrefs((current) => ({ ...current, ...changes }));
  }, []);
  useEffect(() => {
    applyThemeVariables(visualPrefs.theme, visualPrefs.font, visualPrefs.fontSize);
  }, [visualPrefs.theme, visualPrefs.font, visualPrefs.fontSize]);
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) {
      setAuthChecked(true);
      return;
    }
    let parsed = null;
    try {
      parsed = JSON.parse(savedUser);
      setUser(parsed);
    } catch {
      localStorage.removeItem('user');
      setAuthChecked(true);
      return;
    }
    authAPI.getProfile()
      .then((res) => {
        const serverUser = res.data?.data || res.data;
        if (serverUser) {
          const merged = { ...parsed, ...serverUser };
          setUser(merged);
          localStorage.setItem('user', JSON.stringify(merged));
        }
      })
      .catch(() => {
        localStorage.removeItem('user');
        setUser(null);
      })
      .finally(() => setAuthChecked(true));
  }, []);
  const handleLogin = (userData, { fresh = false } = {}) => {
    if (fresh) {
      setLoginGeneration(createLoginGeneration());
    }
    setUser(userData);
  };
  const handleLogout = async () => {
    window.dispatchEvent(new Event('eliteedge:auth-changing'));
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      // Backend serializes the user as { id, ... } not { _id, ... } — tolerate both.
      const uid = u && (u._id || u.id);
      // Wave 1 #9: clear tactics drafts on logout so they don't leak across users on shared devices
      if (uid) clearAllDraftsForUser(uid);
    } catch {}
    try {
      const registration = await navigator.serviceWorker?.getRegistration?.();
      const subscription = await registration?.pushManager?.getSubscription?.();
      if (subscription) {
        const cleanup = Promise.allSettled([
          notificationsAPI.unsubscribeCurrent(subscription.endpoint),
          subscription.unsubscribe(),
        ]);
        await Promise.race([
          cleanup,
          new Promise(resolve => setTimeout(resolve, 2000)),
        ]);
      }
    } catch (error) {
      console.warn('[Notifications] Could not remove this device during logout:', error.message);
    }
    authAPI.logout().catch(() => {});
    localStorage.removeItem('user');
    clearLoginGeneration();
    setLoginGeneration('');
    setUser(null);
  };
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AdminStepUpDialog />
      <UpdateAvailableBanner />
      <Router>
        <CoachExperienceProvider user={authChecked ? user : null}>
        <CoachEntryGate user={authChecked ? user : null} onLogout={handleLogout}>
        {!authChecked ? (
          <div role="status" aria-live="polite" aria-label="Loading Elite Edge AI" style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-primary)', color: 'var(--text-secondary)', fontSize: '14px' }}>
            <BrandLogo variant="icon" height={52} showBeta />
            <span>Loading...</span>
          </div>
        ) : !user ? (
          <RouteSuspense>
          <Routes>
            <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ResetPassword />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/company" element={<CompanyPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/pending-approval" element={<PendingApproval />} />
            <Route path="/privacy-policy" element={<LegalDocumentPage title="Privacy Policy" filePath="/PRIVACY_POLICY.md" />} />
            <Route path="/terms-of-service" element={<LegalDocumentPage title="Terms of Service" filePath="/TERMS_OF_SERVICE.md" />} />
            <Route path="/invite" element={<InviteAcceptPage />} />
            <Route path="/oauth-consent" element={<OAuthConsentPage />} />
            <Route path="/parent-invite/:token" element={<ParentInviteAccept />} />
            <Route path="*" element={<Navigate to="/about" />} />
          </Routes>
          </RouteSuspense>
        ) : (!user.hasClub && user.role !== 'admin' && user.role !== 'parent' && (user.clubGateEnforced || CLUB_ONBOARDING_ENABLED)) ? (
          <Routes>
            <Route path="/invite" element={<InviteAcceptPage isLoggedIn user={user} />} />
            <Route path="/oauth-consent" element={<OAuthConsentPage />} />
            <Route path="/parent-invite/:token" element={<ParentInviteAccept />} />
            <Route path="*" element={CLUB_ONBOARDING_MODE === 'join_or_create'
              ? <ClubOnboarding user={user} onComplete={handleLogin} onLogout={handleLogout} />
              : CLUB_ONBOARDING_MODE === 'create_only'
                ? <CreateClubWizard user={user} onComplete={handleLogin} onLogout={handleLogout} />
                : <NoClubGate user={user} onLogout={handleLogout} />} />
          </Routes>
        ) : user.role === 'parent' ? (
          <>
          <Routes>
            <Route path="/parent" element={
              <Box sx={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1.5, background: 'var(--bg-card)', borderBottom: '1px solid var(--border-input)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <BrandLogo height={32} showBeta />
                    <Typography sx={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600 }}>Member Portal</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Button
                      onClick={() => setParentNotificationsOpen(true)}
                      size="small"
                      sx={{ color: 'var(--accent)', textTransform: 'none', minHeight: 44 }}
                    >
                      Notification settings
                    </Button>
                    <Button onClick={handleLogout} size="small" sx={{ color: 'var(--text-secondary)', textTransform: 'none', minHeight: 44 }}>Logout</Button>
                  </Box>
                </Box>
                <Box sx={{ maxWidth: '900px', margin: '0 auto', p: 2 }}>
                  <RouteSuspense><ParentDashboard /></RouteSuspense>
                </Box>
              </Box>
            } />
            <Route path="/oauth-consent" element={<OAuthConsentPage />} />
            <Route path="/parent-invite/:token" element={<ParentInviteAccept />} />
            {/* A parent invited to coach must reach the club invite accept page. */}
            <Route path="/invite" element={<InviteAcceptPage isLoggedIn user={user} />} />
            <Route path="/privacy-policy" element={<LegalDocumentPage title="Privacy Policy" filePath="/PRIVACY_POLICY.md" />} />
            <Route path="/terms-of-service" element={<LegalDocumentPage title="Terms of Service" filePath="/TERMS_OF_SERVICE.md" />} />
            <Route path="*" element={<Navigate to="/parent" />} />
          </Routes>
          <MobileInstallPrompt
            isMobile={isParentMobile}
            isAuthenticated={Boolean(user)}
            variant="parent"
          />
          <Dialog
            open={parentNotificationsOpen}
            onClose={() => setParentNotificationsOpen(false)}
            fullWidth
            maxWidth="sm"
            aria-labelledby="parent-notification-settings-title"
          >
            <DialogTitle id="parent-notification-settings-title">Notification settings</DialogTitle>
            <DialogContent sx={{ pb: 3 }}>
              <NotificationSettings hideHeading />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setParentNotificationsOpen(false)}
                sx={{ minHeight: 44, textTransform: 'none' }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
          </>
        ) : (
          <TeamProvider>
            <StatConfigProvider>
            <MainLayout onLogout={handleLogout} onVisualSettingsChange={handleVisualSettingsChange} />
            </StatConfigProvider>
          </TeamProvider>
        )}
      {authChecked && user && (
        <NotificationDiscoveryPrompt
          user={user}
          loginGeneration={loginGeneration}
        />
      )}
        </CoachEntryGate>
        </CoachExperienceProvider>
      </Router>
      <SpeedInsights />
    </ThemeProvider>
  );
}
